import React, { useEffect, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { connect } from 'react-redux';
import { ISystem } from '../../helpers/interfaces';
import { showFilteredSystems, showFilteredSystemsNames, updateFilteredSystemsNames } from '../../stores/actions/accountActions';
import { useRef } from 'react';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

interface SystemSearchBarProps {
    systems: ISystem[];
    accountFilteredSystems: number[];
    accountFilteredSystemsNames: string[];
    isFilteredSystemNameUpdated: boolean;
    isSystemSearchBarVisible: boolean;
    showFilteredSystems: (filteredSystems: number[]) => void;
    showFilteredSystemsNames: (filteredSystemsNames: string[]) => void;
    updateFilteredSystemsNames: (isUpdatedName: boolean) => void;
}

const SystemSearchBar = ({
                             systems,
                             accountFilteredSystemsNames,
                             accountFilteredSystems,
                             isFilteredSystemNameUpdated,
                             isSystemSearchBarVisible,
                             showFilteredSystems,
                             showFilteredSystemsNames,
                             updateFilteredSystemsNames,
                         }: SystemSearchBarProps) => {
    const systemNames = [...systems].map((system: ISystem) => system.name);
    const systemOptions = systemNames.sort();
    const ref = useRef();

    useEffect(() => {
        // check if some system has been updated and user has favorites systems:
        if (isFilteredSystemNameUpdated && accountFilteredSystems?.length) {
            // systems references after update:
            const currentPickedSystems: ISystem[] = [];
            accountFilteredSystems.forEach((filteredSystemId) => {
                const currentSystemReference = systems.find(({ ID }) => ID === filteredSystemId);
                if (currentSystemReference) {
                    currentPickedSystems.push(currentSystemReference);
                }
            });
            // updated systems names:
            const currentPickedSystemsNames: string[] = currentPickedSystems.map((system) => system.name);
            showFilteredSystemsNames(currentPickedSystemsNames);
            updateFilteredSystemsNames(false);
        }
    }, [isFilteredSystemNameUpdated, accountFilteredSystemsNames, accountFilteredSystems, showFilteredSystemsNames, systems, updateFilteredSystemsNames]);

    useEffect(() => {
        updateFilteredSystemsNames(true);
    }, [updateFilteredSystemsNames, systems.length]);

    useEffect(() => {
        ref.current.focus();
    }, [isSystemSearchBarVisible]);

    const handleSelectChange = (e, pickedSystemsNames) => {
        showFilteredSystemsNames(pickedSystemsNames);
        const pickedSystemsIds: number[] = [];
        pickedSystemsNames.forEach((pickedSystemName: string) => {
            const [currentSystem] = systems
                // return system id using system name:
                .map((system) => {
                    if (system.name === pickedSystemName) {
                        return system.ID;
                    }
                    return null;
                })
                // remove undefined values:
                .filter((value) => value !== null);
            if (currentSystem) {
                pickedSystemsIds.push(currentSystem);
            }
        });
        showFilteredSystems(pickedSystemsIds);
    };
    const [inputValue, setInputValue] = useState("");
    return (
        <Autocomplete
            multiple
            id='checkboxes-systems'
            options={systemOptions}
            value={accountFilteredSystemsNames}
            inputValue={inputValue}
            disableCloseOnSelect
            getOptionLabel={(system) => system}
            renderOption={(system, { selected }) => (
                <>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8, color: 'gray' }} checked={selected} />
                    {system}
                </>
            )}
            style={{
                backgroundColor: 'rgba(250,250,250,0.75)',
                transition: '0.3s',
                margin: isSystemSearchBarVisible ? '0 15px 15px 15px' : '0 15px',
                height: isSystemSearchBarVisible ? 'auto' : '0',
                opacity: isSystemSearchBarVisible ? '1' : '0',
            }}
            renderInput={(systems) =>
                <TextField
                    {...systems}
                    variant='outlined'
                    label='Systems search bar'
                    placeholder='Favorites'
                    inputRef={ref}
                    onChange={(e) => setInputValue(e.target.value)}
                />
            }
            onChange={(e, pickedSystems) => handleSelectChange(e, pickedSystems)}
            fullWidth
            disabled={!isSystemSearchBarVisible}
            clearOnBlur={true}
        />
    );
};

const mapStateToProps = (state) => {
    return {
        accountFilteredSystems: state.account.accountFilteredSystems,
        accountFilteredSystemsNames: state.account.accountFilteredSystemsNames,
        isFilteredSystemNameUpdated: state.account.isAccountFilteredSystemNameUpdated,
        isSystemSearchBarVisible: state.account.isAccountSystemSearchBarVisible,
    };
};

const actions = {
    showFilteredSystems,
    showFilteredSystemsNames,
    updateFilteredSystemsNames,
};

export default connect(mapStateToProps, actions)(SystemSearchBar);
